import React from "react";
import QuoteBlock from "./QuoteBlock";
import ChapterLinkCard from "./ChapterLinkCard";

/**
 * NarrativeSection (ordered)
 * Pass everything through a single `content` array to control order.
 * Each item can be:
 *  - string | JSX  → renders as a paragraph
 *  - { type: 'quote', content, tone?, align?, className? }
 * Props:
 *  - accentColor → sets the main color theme (default: "#1d4ed8" → Tailwind blue-700)
 */
export default function NarrativeSection({
    id,
    className = "",
    containerWidth = "max-w-3xl",
    padY = "py-16",
    padX = "px-6 sm:px-12",
    centeredImage, // { src, alt, widthClass }
    heading,       // string
    headingClass = "",
    content = [],  // ordered array (strings/JSX or {type:'quote', ...})
    skipTo,        // { href, label, thumbSrc?, thumbAlt? }
    accentColor = "#1d4ed8", // 👈 default blue (Tailwind blue-700)
}) {
    return (
        <section
            id={id}
            className={`w-full ${padY} ${padX} ${className}`}
            style={{ "--accent-color": accentColor }}
        >
            {(centeredImage || heading) && (
                <div className="text-center mb-12 flex flex-col items-center">
                    {centeredImage?.src && (
                        <img
                            src={centeredImage.src}
                            alt={centeredImage.alt || ""}
                            className={`${centeredImage.widthClass || "w-[60%] sm:w-[70%]"} max-w-full mb-6`}
                        />
                    )}
                    {heading && (
                        <h3
                            className={`font-semibold text-xl sm:text-5xl`}
                            style={{ color: "var(--accent-color)" }}
                        >
                            {heading}
                        </h3>
                    )}
                </div>
            )}

            <div
                className={`${containerWidth} mx-auto text-left text-gray-900 text-[17px] leading-relaxed`}
            >
                {content.map((item, i) => {
                    // Quote item
                    if (item && typeof item === "object" && item.type === "quote") {
                        return (
                            <QuoteBlock
                                key={`q-${i}`}
                                tone={item.tone}
                                align={item.align}
                                className={item.className}

                            >
                                {item.content}
                            </QuoteBlock>
                        );
                    }
                    // Paragraph (string or JSX)
                    return (
                        <p key={`p-${i}`} className="mb-6">
                            {item}
                        </p>
                    );
                })}

                {skipTo && (
                    <div className="mt-10">
                        <hr
                            className="border-t my-10"
                            style={{ borderColor: "var(--accent-color)" }}
                        />
                        <ChapterLinkCard
                            href={skipTo.href}
                            label={skipTo.label}
                            thumbSrc={skipTo.thumbSrc}
                            thumbAlt={skipTo.thumbAlt}
                        />
                    </div>
                )}
            </div>
        </section>
    );
}
